import { animate, state, style, transition, trigger } from '@angular/animations';
import { DatePipe } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ChartAttributes } from 'src/app/model/shared/components/entities/entities-profile/chart-builder';
import { DataService, GeneralService, ServicecallService } from 'src/app/services';

@Component({
  selector: 'app-playback-analysis-drawer',
  templateUrl: './playback-analysis-drawer.component.html',
  styleUrl: './playback-analysis-drawer.component.scss',
  animations: [
    trigger('slideInOut', [
      state('in', style({ transform: 'translateX(0)' })),
      transition('void => *', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms ease-in-out'),
      ]),
      transition('* => void', [
        animate('300ms ease-in-out', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
    trigger('fadeInOut', [
      state('in', style({ opacity: 1 })),
      transition('void => *', [
        style({ opacity: 0 }),
        animate('200ms ease-in-out'),
      ]),
      transition('* => void', [
        animate('200ms ease-in-out', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class PlaybackAnalysisDrawerComponent {
  @Input() entityName: string;
  @Input() isOpen = false;
  @Input() telemetryTableData: any;
  @Input() alertsHighlighted: any[] = [];
  @Input() chartSelectedDate: any;
  @Input() chartTypes: any[] = [];
  @Input() selectedDate: { tripId: string, start: string, end: string } | Date = null;
  @Input() entityId: any;
  @Input() transformedAttributeData: any;
  @Input()
  set date(value: Date | { tripId: string, start: string, end: string }) {
    if (value) {
      this._date = value;
      this._activeAlertsCountData();
    }
  }
  get date() {
    return this._date;
  }

  @Input()
  set aggregateDate(value: { from: Date; to: Date }) {
    if (value) {
      this._aggregateDate = value;
      this._activeAlertsCountData();
    }
  }
  get aggregateDate() {
    return this._aggregateDate;
  }

  @Output() closeDrawer = new EventEmitter<void>();
  @Output() selectedAlert = new EventEmitter<void>();

  activeView: 'table' | 'chart' = 'table';
  telemetryColumHeader: any = {};
  alertColumnHeader: any = { 'time': 'Time', 'alert': 'Alert Name', 'message': 'Alert Description', 'acknowledged': 'Acknowledged' };
  activeAlertsCount: number;
  chartType = new FormControl();
  chartAttributes: {
    chartAttributes: ChartAttributes, selectedDates: {
      from: Date,
      to: Date,
    },
    order?: number
  };

  private _dataServiceSubscription: Subscription;
  private _getAlertsCountSubscription: Subscription;
  private _date: Date | { tripId: string, start: string, end: string };
  private _filterLists: any[] = [];
  private _aggregateDate: { from: Date; to: Date };
  private _tenantConfig: any;

  constructor(private _generalService: GeneralService,
    private _datePipe: DatePipe, private _dataSer: DataService, private _sharedService: ServicecallService) {
    this._dataServiceSubscription = this._dataSer.events.subscribe((res: any) => {
      if (res.name === 'apiRefresh' || res.name === 'siteChanged') {
        this._activeAlertsCountData();
      }
    });
  }

  ngOnInit(): void {
    if (this.isOpen) {
      document.body.style.overflow = 'hidden';
    }
    this._tenantConfig = this._sharedService?.getConfiguration('tenantGenericConfig') ? this._sharedService?.getConfiguration('tenantGenericConfig') : null;
    this.telemetryColumHeader = this._tenantConfig?.geoTelemetryHeader ?? {};
    this.alertColumnHeader = this._tenantConfig?.geoAlertHeader ?? {};
  }

  ngOnDestroy(): void {
    document.body.style.overflow = 'auto';
    this._dataServiceSubscription?.unsubscribe();
    this._getAlertsCountSubscription?.unsubscribe();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isOpen) {
      this.onClose();
    }
  }

  onChartTypeChange(event) {
    this.chartAttributes = {
      chartAttributes: this.chartType?.value, selectedDates: {
        from: this.chartSelectedDate?.[0],
        to: this.chartSelectedDate?.[1]
      }
    }
  }

  onClose(): void {
    document.body.style.overflow = 'auto';
    this.closeDrawer.emit();
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  setActiveView(view: 'table' | 'chart'): void {
    this.activeView = view;
  }

  isTableViewActive(): boolean {
    return this.activeView === 'table';
  }

  isChartViewActive(): boolean {
    return this.activeView === 'chart';
  }

  selectedAlertUpdate(alert: any) {
    this.selectedAlert.emit(alert);
  }

  private _activeAlertsCountData() {
    let configStartDate = null;
    let configEndDate = null;
    this._tenantConfig = this._sharedService?.getConfiguration(
      'tenantGenericConfig'
    )
      ? this._sharedService?.getConfiguration('tenantGenericConfig')
      : null;

    if (this._tenantConfig?.defaultTimeRange) {
      const configHour = this._tenantConfig?.defaultTimeRange || 0;

      const { startDate, endDate } =
        this._sharedService.calculateDateRange(configHour);
      configEndDate = endDate;
      configStartDate = startDate;
    }
    let startTime;
    let endTime;

    //For Chart
    this.chartSelectedDate = [new Date(this.aggregateDate?.from ?? ((!this.selectedDate && configStartDate) ? configStartDate : startTime)), new Date(this.aggregateDate?.to ?? ((!this.selectedDate && configEndDate) ? configEndDate : endTime))];
    this.chartType?.setValue(this.chartTypes?.[0]?.value);
    this.chartAttributes = {
      chartAttributes: { ...this.chartTypes?.[0]?.value, ...{ entityNames: [{ _id: this.entityId, uniqueId: this.entityName }] } }, selectedDates: {
        from: this.chartSelectedDate?.[0],
        to: this.chartSelectedDate?.[1]
      }
    }

    if (this._tenantConfig?.tripHistoryProfilingBasedOn === 'tripId') {
      if (this.date) {
        startTime = this.date?.['start'];
        endTime = this.date?.['end'];
      } else {
        [startTime, endTime] = this._generalService?.getStartEndTimeByDate() ?? [];
      }
    } else {
      if (this.date) {
        [startTime, endTime] = this._generalService?.getStartEndTimeForSelectedDate(
          this.date,
          this._tenantConfig?.tenant,
          this._tenantConfig?.defaultTimeRange
        ) ?? [];
      } else {
        [startTime, endTime] = this._generalService?.getStartEndTimeByDate() ?? [];
      }
    }
    this._filterLists = [{ type: 'Status', value: 'active' }];
    const reqData = {
      filterList: this._filterLists,
      from: this._datePipe.transform(
        this._aggregateDate?.from ??
        (!this.date && configStartDate ? configStartDate : startTime),
        'yyyy-MM-ddTHH:mm:ssZZ'
      ),
      to: this._datePipe.transform(
        this._aggregateDate?.to ??
        (!this.date && configEndDate ? configEndDate : endTime),
        'yyyy-MM-ddTHH:mm:ssZZ'
      ),
      siteId: this._sharedService.getSiteId(),
      entityId: this.entityId,
    };
    this._getAlertsCountSubscription = this._sharedService
      .getAlertsCount(reqData, false)
      .subscribe((result: any) => {
        if (result['status']) {
          const status = result['response']['data'].find(
            (item: any) => item.status
          )?.status;
          if (status) {
            this.activeAlertsCount = status
              ? status.find((item: any) => item.status)?.count
              : undefined;
          }
        } else {
          this._sharedService.toasterService({
            type: 'error',
            msg: result?.response?.message,
          });
        }
      });
  }
}
